import { useEffect, useState } from 'react';
import type { ScopedAccessLevel, User } from '@/types';
import { getCapabilitiesFor } from '@/lib/auth/capabilities';
import {
  findCanonicalRouteRule,
  getAuthorityBackedRouteAccess,
  getMvpRouteAccess,
  getUserRouteAccess,
  resolveUserMvpAccountRole,
  type RouteAccessDecision,
} from '@/lib/auth/routeRegistry';
import { resolveRouteAuthority } from '@/lib/auth/routeAuthority';
import { supabase } from '@/lib/supabase';

interface RouteResource {
  resourceType: 'project' | 'investment' | 'report';
  resourceId: string;
}

export function getRouteResource(path: string): RouteResource | null {
  const segments = path.split('/').filter(Boolean);
  if (segments[0] === 'projects' && segments[1]) {
    return { resourceType: 'project', resourceId: segments[1] };
  }
  if (segments[0] === 'investments' && segments[1]) {
    return { resourceType: 'investment', resourceId: segments[1] };
  }
  if (segments[0] === 'investor' && segments[1] === 'reports' && segments[2]) {
    return { resourceType: 'report', resourceId: segments[2] };
  }
  return null;
}

export function useMvpRouteAccess(
  user: User | null,
  path: string,
): { decision: RouteAccessDecision; loading: boolean } {
  const resource = getRouteResource(path);
  const resourceType = resource?.resourceType;
  const resourceId = resource?.resourceId;
  const rule = findCanonicalRouteRule(path);
  const immediateDecision = rule?.authorityMode === 'resource_scoped' && !resource
    ? { allowed: true, registered: true, rule, reason: 'resource_scoped_deferred' }
    : getUserRouteAccess(user, path);
  const [state, setState] = useState<{ decision: RouteAccessDecision; loading: boolean }>({
    decision: immediateDecision,
    loading: Boolean(resource && resolveUserMvpAccountRole(user)),
  });

  useEffect(() => {
    const role = resolveUserMvpAccountRole(user);
    const currentRule = findCanonicalRouteRule(path);
    if ((!resourceType && currentRule?.authorityMode === 'resource_scoped') || !resourceType || !resourceId || !role) {
      setState({ decision: getUserRouteAccess(user, path), loading: false });
      return;
    }

    if (!supabase || !user?.workspace_id) {
      setState({ decision: getMvpRouteAccess({ role, capabilities: getCapabilitiesFor(role), path }), loading: false });
      return;
    }

    let cancelled = false;
    setState(current => ({ ...current, loading: true }));

    void (async () => {
      const { data: relationships, error: relationshipError } = await supabase
        .from('scoped_relationships')
        .select('workspace_id, access_level')
        .eq('user_id', user.id)
        .eq('workspace_id', user.workspace_id)
        .eq('resource_type', resourceType)
        .eq('resource_id', resourceId)
        .eq('status', 'active')
        .order('updated_at', { ascending: false })
        .limit(1);

      const relationship = relationships?.[0] as {
        workspace_id: string;
        access_level: ScopedAccessLevel;
      } | undefined;
      let entitled = false;
      if (!relationshipError && relationship) {
        const { data, error } = await supabase.rpc('ax1_has_scoped_access', {
          p_workspace_id: user.workspace_id,
          p_resource_type: resourceType,
          p_resource_id: resourceId,
          p_required_access_level: 'view',
        });
        entitled = !error && data === true;
      }

      if (cancelled) return;
      const authority = resolveRouteAuthority(user);
      setState({
        decision: getAuthorityBackedRouteAccess(authority, path, {
          currentWorkspaceId: user.workspace_id,
          resource: relationship ? {
            workspaceId: relationship.workspace_id,
            accessLevel: relationship.access_level,
            requiredAccessLevel: 'view',
            entitled,
          } : null,
        }),
        loading: false,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [path, resourceId, resourceType, user]);

  return state;
}
